import React, {useEffect, useState} from "react";
import {SpokenLine, useSpeech} from "../../common/spokenLine";
import {HoldToTalk} from "../voice/hold_to_talk";
import {AnimatedPoints, HighScoresControl} from "../../common/scores";

// The bargain runs on the server (server/games/devils_bargain/director.rb): it summons, speaks each line and
// gives the verdict tableau its go. Screens show it.
export const DevilsBargain = ({isControl, receiveEvents, clockDrift}) => {
    const [state, setState] = useState(null);
    // True while the persona is speaking on the big screen: set when a new
    // line lands, cleared by the server's line-done broadcast (or a
    // length-based failsafe so a lost packet can't brick the talk button).
    const [personaSpeaking, setPersonaSpeaking] = useState(false);
    const lastLineRef = React.useRef(null);
    const failsafeRef = React.useRef(null);

    const refresh = () => fetch('/bargain/state', {credentials: 'include'})
        .then(r => r.json()).then(setState).catch(() => {});

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 2500);
        const unsubscribe = receiveEvents ? receiveEvents((data) => {
            if (data && data.bargainLineDone) setPersonaSpeaking(false);
            if (data && data.update) refresh();
        }) : null;
        return () => { clearInterval(id); if (unsubscribe) unsubscribe(); };
    }, []);

    useEffect(() => {
        const line = state && state.currentLine;
        if (!line || line === lastLineRef.current) return;
        const isFirst = lastLineRef.current === null;
        lastLineRef.current = line;
        if (isControl || isFirst) return; // joining mid-line: don't lock the button
        setPersonaSpeaking(true);
        if (failsafeRef.current) clearTimeout(failsafeRef.current);
        failsafeRef.current = setTimeout(() => setPersonaSpeaking(false), 3000 + line.length * 80);
    }, [state, isControl]);

    if (isControl) return <BargainControl state={state} receiveEvents={receiveEvents} clockDrift={clockDrift}/>;
    return <BargainPlayer state={state} onSpoke={refresh} personaSpeaking={personaSpeaking}/>;
};

const BargainControl = ({state, receiveEvents, clockDrift}) => {
    const speech = useSpeech(receiveEvents);
    const [leaderboard, setLeaderboard] = useState(null);
    const verdictSpoken = !!(state && state.verdictGo);
    const showBoard = !!(state && state.board);
    const showTableau = !!(state && state.phase === 'done' && state.transfer);
    // Orb sinks while the lower screen is occupied (board or verdict tableau).
    // Start = wherever the last animation ended, so scene entry begins UP
    // (no rise-from-the-bottom) and phase changes still glide.
    const orbLow = showBoard || showTableau;
    const orbTarget = orbLow ? 0.10 : 0.42;
    const orbLastTarget = React.useRef(0.42);
    const orbStart = React.useRef(0.42);
    if (orbLastTarget.current !== orbTarget) {
        orbStart.current = orbLastTarget.current;
        orbLastTarget.current = orbTarget;
    }

    useEffect(() => {
        if (!showBoard) { setLeaderboard(null); return; }
        fetch('/leaderboard/get-leaderboard?type=end&limit=10')
            .then(r => r.json()).then(setLeaderboard).catch(() => {});
    }, [showBoard]);

    if (!state) return null;

    return <>
        <div style={{position: 'fixed', inset: 0, backgroundColor: '#000', zIndex: -1}}/>
        <div style={{position: 'relative', width: '1500px', height: '900px', alignSelf: 'flex-start'}}>
            <div style={{fontSize: (showBoard || showTableau) ? 40 : 70, textAlign: 'center', width: '100%'}}>
                <SpokenLine clockDrift={clockDrift} speech={speech && speech.id === state.lineId ? speech
                    : state.lineDone && state.currentLine ? {text: state.currentLine, endedAt: true} : null}/>
            </div>
            {showBoard && leaderboard && <div style={{position: 'absolute', bottom: -60, left: 0, right: 0, transform: 'scale(0.82)', transformOrigin: 'bottom center'}}>
                <HighScoresControl title="" highScores={leaderboard}/>
            </div>}
            {showTableau && <div style={{position: 'absolute', bottom: 100, left: 0, right: 0}}>
                <TransferTableau
                    selectee={state.selectee}
                    victim={state.victim}
                    transfer={state.transfer}
                    go={verdictSpoken}
                />
            </div>}
        </div>
    </>;
};

const TransferTableau = ({selectee, victim, transfer, go}) => {
    // Points flow victim -> selectee when granted; reversed on the backfire.
    const arrowLeft = !transfer.granted;
    const card = (player, before, after) => <div style={{textAlign: 'center', width: 480}}>
        <img src={player.image_url} alt={player.name} style={{width: 300, height: 300, objectFit: 'cover', objectPosition: 'top'}}/>
        <div style={{fontSize: 52, fontWeight: 800, lineHeight: 1.1, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>{player.name}</div>
        <AnimatedPoints from={before} to={after} go={go}/>
    </div>;

    return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 30, marginTop: 30}}>
        {card(victim, transfer.victimBefore, transfer.victimAfter)}
        <div style={{textAlign: 'center', width: 240}}>
            <div style={{fontSize: 64, fontWeight: 900, color: '#ff5a3a', textShadow: '0 0 20px rgba(255,60,40,0.7)'}}>
                {transfer.amount}
            </div>
            <div style={{
                fontSize: 80,
                color: '#ff5a3a',
                textShadow: '0 0 24px rgba(255,60,40,0.8)',
                animation: go ? 'none' : 'pulse 1.2s ease-in-out infinite',
                transform: arrowLeft ? 'scaleX(-1)' : 'none',
            }}>➤</div>
        </div>
        {card(selectee, transfer.selecteeBefore, transfer.selecteeAfter)}
    </div>;
};

const BargainPlayer = ({state, onSpoke, personaSpeaking}) => {
    if (!state) return null;

    if (state.phase === 'idle') return <Backdrop>
        <div style={{fontSize: 24, opacity: 0.6}}>Something stirs…</div>
    </Backdrop>;

    if (!state.isSelectee) {
        const victimized = state.isVictim && state.phase === 'done' && state.transfer;
        return <Backdrop>
            {state.selectee && <>
                <img src={state.selectee.image_url} alt={state.selectee.name} style={{
                    width: '96%', maxWidth: 500, aspectRatio: '1', objectFit: 'cover', objectPosition: 'top',
                    filter: 'drop-shadow(0 0 24px rgba(255,40,20,0.45))',
                }}/>
                <div>
                    <div style={{fontSize: 58, fontWeight: 900, lineHeight: 1.1, color: '#ff9d8a', textShadow: '0 0 24px rgba(255,60,40,0.6)'}}>
                        {state.selectee.name}
                    </div>
                    {state.selectee.nickname && <div style={{fontSize: 30, fontStyle: 'italic', opacity: 0.85}}>
                        {state.selectee.nickname}
                    </div>}
                </div>
            </>}
            {victimized && <div style={{fontSize: 24, fontWeight: 800, color: state.transfer.granted ? '#ff5a3a' : '#7dff96'}}>
                {state.transfer.granted
                    ? `${state.selectee.name} took ${state.transfer.amount} points from YOU.`
                    : `${state.selectee.name} tried to rob you. The Machine gave you ${state.transfer.amount} points instead.`}
            </div>}
        </Backdrop>;
    }

    if (state.phase === 'done') return <Backdrop>
        <div style={{fontSize: 30, fontWeight: 900, color: '#ff9d8a'}}>The bargain is sealed.</div>
        {state.transfer && <div style={{
            fontSize: 56, fontWeight: 900,
            color: state.transfer.granted ? '#7dff96' : '#ff5a3a',
            textShadow: '0 0 24px rgba(0,0,0,0.6)',
        }}>
            {state.transfer.granted ? `+${state.transfer.amount}` : `−${state.transfer.amount}`}
        </div>}
    </Backdrop>;

    const PROMPTS = {
        presence: 'Say hi.',
        choice: 'Give points away, or take from someone else?',
        victim: 'Who do you want to take points from?',
        amount: 'How many points do you want to take?',
        justify: 'Tell everyone why you deserve them.',
    };

    return <Backdrop>
        {PROMPTS[state.phase] && <div style={{fontSize: 26, fontWeight: 700, lineHeight: 1.35, maxWidth: 340, textShadow: '1px 1px 0 rgba(0,0,0,0.9)'}}>
            {PROMPTS[state.phase]}
        </div>}
        <HoldToTalk
            expect={state.expect}
            uploadTo="/bargain/speak"
            onResult={onSpoke}
            variant="evil"
            disabled={personaSpeaking}
        />
    </Backdrop>;
};

const Backdrop = ({children}) => <div style={{
    width: '100%',
    minHeight: '100dvh',
    boxSizing: 'border-box',
    backgroundColor: '#0a0000',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    padding: 20,
    textAlign: 'center',
}}>{children}</div>;
